import { Box, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import LinkCatalogueSection, { type OutletField } from "./LinkCatalogueSection";
import { refusalCopy } from "./linkRefusalCopy";
import { tokens } from "../../theme";

/**
 * The PMS card on Promotions → Offers: which of the hotel's PMS services feed
 * the TV, and "Sync now" (Sigge, 2026-10-28). It moved here from the
 * connection's card under PMS → Integrations, so the hotel runs its own offers
 * import; connecting the PMS stays Nenda's.
 *
 * Renders nothing unless the hotel has its own connection that imports a menu
 * (`GET /link/menu-import` answers `connection: null` otherwise).
 */

interface MenuImport {
  connection: {
    id: string;
    vendor: string;
    displayName: string;
    hasCredentials: boolean;
  } | null;
  outletField?: OutletField | null;
  picked?: string;
  readMenu?: boolean;
}

export default function PmsOffersImport({
  premiseId,
  canEdit,
  onSynced,
}: {
  premiseId: string;
  canEdit: boolean;
  /** A sync created or updated offers: the list reloads. */
  onSynced: () => void;
}) {
  const { t } = useTranslation(["premises", "common"]);
  const [data, setData] = useState<MenuImport | null>(null);
  const refusal = useCallback(
    (code: string | null) => refusalCopy(t, code),
    [t]
  );

  const load = useCallback(async () => {
    try {
      const res = await fetch(
        `/api/cms/v1/premise/${premiseId}/link/menu-import`,
        { credentials: "include" }
      );
      // Refused or failed: no card, rather than a broken Offers page.
      setData(res.ok ? ((await res.json()) as MenuImport) : null);
    } catch {
      setData(null);
    }
  }, [premiseId]);

  useEffect(() => {
    void load();
  }, [load]);

  const connection = data?.connection;
  if (!connection) return null;
  // The adapter names its environment in English ("Mews (demo environment)");
  // the suffix is said in the operator's language.
  const name = connection.displayName.replace(
    / \(demo environment\)$/,
    ` (${t("pmsOffersImport.demoEnvironment")})`
  );

  return (
    <Box
      data-testid="offers-pms-import"
      sx={{
        border: `1px solid ${tokens.divider}`,
        borderRadius: "12px",
        p: 2,
        mb: 2,
      }}
    >
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {t("pmsOffersImport.title", { name })}
      </Typography>
      <LinkCatalogueSection
        premiseId={premiseId}
        connectionId={connection.id}
        vendor={connection.vendor}
        displayName={name}
        picked={data?.picked ?? ""}
        hasCredentials={connection.hasCredentials}
        canEdit={canEdit}
        readMenu={Boolean(data?.readMenu)}
        outletField={data?.outletField ?? null}
        refusalCopy={refusal}
        onSaved={load}
        onSynced={onSynced}
      />
    </Box>
  );
}
